import * as React from 'react';
import {ChangeEvent, useEffect, useState} from 'react';
import {faPlus} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useMediaQuery} from 'usehooks-ts';

import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {CheckBox} from '../../ui/forms/CheckBox/CheckBox';
import {DisplayCurrencyFormatted} from '../../orders/display-currency-formatted/DisplayCurrencyFormatted';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {ItemsService} from '../../../client/items/items.service';
import {ItemToAdd} from '../../order-items/order-items.class';
import {OrderableItem} from '../item.class';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import Button from '../../ui/Buttons/Button';

export interface FrequentlyBoughtTogetherProps {
    itemTotal: number;
    orderableItems: OrderableItem[];
    user: User;
}

export const FrequentlyBoughtTogether = ({itemTotal, orderableItems, user}: FrequentlyBoughtTogetherProps) => {
    const [itemTotalError, setItemTotalError] = useState(``);
    const [itemTotalState, setItemTotalState] = useState(itemTotal);
    const [numItemsCheckedState, setNumItemsCheckedState] = useState(orderableItems.length);
    const componentName = `FrequentlyBoughtTogether`;
    const gaItemListName: GaItemListName = `FrequentlyBoughtTogether`;
    const isMobile = useMediaQuery(`(max-width: 1024px)`);
    const itemsService: ItemsService = useService(`itemsService`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);
    const userStateService: UserStateService = useService(`userStateService`);
    const [orderableItemsState, setOrderableItemsState] = useState(
        orderableItems?.map((orderableItem) => {
            orderableItem.checked = true;
            return orderableItem;
        }),
    );

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            orderItemsWorkflow.addToOrderModal(userStateService.addToOrderItems, componentName, true);
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Adds checked items to order
     */
    const addToOrder = () => {
        const itemsToAdd: ItemToAdd[] = [];
        for (const orderableItem of orderableItemsState) {
            if (orderableItem.checked) {
                itemsToAdd.push({
                    gaEcommerce: {item_list_name: gaItemListName},
                    item: orderableItem.item,
                    unitsOrdered: 1,
                });
            }
        }
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);
    };

    /**
     * Updates orderableItemsState when checked
     * @param item
     * @param checked
     */
    const recordItemCheckbox = (item: OrderableItem, checked: boolean) => {
        let numItemsChecked = 0;
        const updatedOrderableItems: OrderableItem[] = orderableItemsState.map((orderableItem) => {
            // Handle item checked
            if (item.item === orderableItem.item) {
                if (checked) {
                    numItemsChecked++;
                }
                return {...item, checked};
            }

            // Handle state for other items
            if (orderableItem.checked) {
                numItemsChecked++;
            }
            return orderableItem;
        });

        // Update state
        setNumItemsCheckedState(numItemsChecked);
        setOrderableItemsState(updatedOrderableItems);

        // Get updated total, if logged in
        if (user.isLoggedIn()) {
            setItemTotalError(``);
            const checkedItemNums = [];
            for (const orderableItem of updatedOrderableItems) {
                if (orderableItem.checked) {
                    checkedItemNums.push(orderableItem.item);
                }
            }
            itemsService
                .getManyPrices(checkedItemNums)
                .then((getManyPricesRes) => {
                    setItemTotalState(getManyPricesRes.itemTotal);
                })
                .catch((addPricesForItemTypeArrayErr: ImpError) => {
                    setItemTotalError(addPricesForItemTypeArrayErr.message);
                });
        }
    };

    /**
     * Template
     */
    return (
        <>
            <PageHeader
                title="Frequently Bought Together"
                type="h4"
            />
            <div className="tw-flex tw-justify-start lg:tw-justify-between tw-items-start lg:tw-items-center tw-flex-col lg:tw-flex-row">
                <div className="tw-flex">
                    {orderableItemsState.map((orderableItem, index) => (
                        <div
                            className="tw-flex tw-flex-nowrap tw-items-center"
                            key={orderableItem.prettyItem}
                        >
                            <ProductImage
                                altText={orderableItem.description}
                                responsive={true}
                                size={120}
                                src={orderableItem.imagePath}
                            />
                            {orderableItemsState.length - 1 !== index && (
                                <FontAwesomeIcon
                                    className="tw-mx-4"
                                    icon={faPlus}
                                />
                            )}
                        </div>
                    ))}
                </div>
                <div className="tw-mx-0 lg:tw-mx-6">
                    <div
                        className="tw-ml-10"
                        style={{fontStyle: `italic`}}
                    >
                        Currently Viewing
                    </div>
                    {orderableItemsState.map((orderableItem, index) => (
                        <div
                            className="tw-flex tw-mb-4"
                            key={index}
                        >
                            <CheckBox
                                alias={`frequentlyBoughtTogether__${orderableItem.item}`}
                                checked={orderableItem.checked}
                                className="tw-mb-0"
                                disabled={index === 0}
                                name={`frequentlyBoughtTogether__${orderableItem.item}`}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    recordItemCheckbox(orderableItem, e.target.checked);
                                }}
                            />
                            <div>
                                <div>
                                    {index === 0 ? (
                                        <div>{orderableItem.description}</div>
                                    ) : (
                                        <ImpressionTracker
                                            as="span"
                                            data={{
                                                dimension16: ordersService?.currentOrderNumber,
                                                index,
                                                item_id: orderableItem.item,
                                                item_list_name: componentName,
                                            }}
                                        >
                                            <a href={`/item/${orderableItem.item}`}>{orderableItem.description}</a>
                                        </ImpressionTracker>
                                    )}
                                </div>
                                <div className="tw-flex tw-items-center">
                                    <Price
                                        alias={componentName}
                                        doNotShowEachPrice={true}
                                        item={orderableItem}
                                        user={user}
                                    />
                                    &nbsp;| #{orderableItem.prettyItem} | Pkg Qty: {orderableItem.pkgQty}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={`tw-flex tw-flex-col tw-text-left lg:tw-text-center ${isMobile ? 'tw-w-full' : ''}`}>
                    {user.isLoggedIn() && (
                        <>
                            Total Price:
                            <div className="tw-mb-6 tw-text-2xl tw-font-bold tw-leading-[1.2]">
                                {itemTotalError ? <>{itemTotalError}</> : <DisplayCurrencyFormatted amount={itemTotalState} />}
                            </div>
                        </>
                    )}
                    <Button
                        className="tw-w-full lg:tw-w-auto tw-mb-4 lg:tw-mb-0"
                        onClick={addToOrder}
                        size="smToLg"
                        variant="primary"
                    >
                        Add {numItemsCheckedState} Items to Order
                    </Button>
                </div>
            </div>
        </>
    );
};
